import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";

import { Grid, Button, useTheme, ListItem, Table, TableHead } from "@mui/material";
import { Refresh, Save, Translate } from "@mui/icons-material";

import { LoadingScreen } from "../../../partials/LoadingScreen";
import StyledTextField from "../../../styled/TextField";
import StyledTableCell from "../../../styled/TableCell";
import StyledTableRow from "../../../styled/TableRow";

import apiClient from "../../../../services/api";
import store from "../../../../store";
import { toggleMessageOpen } from "../../../../store/actions/Message";

/**
 * Translation table component
 * @param {*} props 
 * @returns 
 */
const TranslationTable = (props) => {
    const { translations, translationChanged } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const Row = ({ index, style }) => {
        return (
            <ListItem
                style={style}
                key={`project`+index}
                component="div"
                disablePadding
                sx={{
                    backgroundColor: index % 2 === 0 ? theme.palette.background.secondary : '',
                    borderBottom: '1px solid ' + theme.palette.storeDrawer.tabs.list.borderBottom.color,
                    overflowAnchor: 'none'
                }}
            >
                <Grid container spacing={1}>
                    <Grid item xs={4}>{translations[index].key}</Grid>
                    <Grid item xs={4}>
                        <StyledTextField
                            fullWidth
                            multiline
                            variant="outlined"
                            size="small"
                            inputProps={{ languagecode: "en" }}
                            id={"en-" + index}
                            name={translations[index].key}
                            defaultValue={translations[index].en}
                            onBlur={(event) => translationChanged(event, translations[index].key, 'en')}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <StyledTextField
                            fullWidth
                            multiline
                            variant="outlined"
                            size="small"
                            inputProps={{ languagecode: "de" }}
                            id={"de-" + index}
                            name={translations[index].key}
                            defaultValue={translations[index].de}
                            onBlur={(event) => translationChanged(event, translations[index].key, 'de')}
                        />
                    </Grid>
                </Grid>
            </ListItem>
        );
    }

    return (
        <>
            <Table stickyHeader aria-label="translations">
                <TableHead>
                    <StyledTableRow>
                        <StyledTableCell style={{width: '33.33%'}}>{t("Key")}</StyledTableCell>
                        <StyledTableCell style={{width: '33.33%'}}>{("English")}</StyledTableCell>
                        <StyledTableCell style={{width: '33.33%'}}>{t("German")}</StyledTableCell>
                    </StyledTableRow>
                </TableHead>
            </Table>
            <AutoSizer style={{ width: '100%' }} disableWidth>
                {({ height }) => (
                    <FixedSizeList height={height - 100} itemSize={73} itemCount={translations.length}>
                        {Row}
                    </FixedSizeList>
                )}
            </AutoSizer>
        </>
    );
}

/**
 * Translations page
 * @returns 
 */
function Translations()
{
    const componentMounted = useRef(true);
    const { t } = useTranslation();

    const [englishTranslations, setEnglishTranslations] = useState(null);
    const [germanTranslations, setGermanTranslations] = useState(null);
    const [translations, setTranslations] = useState(null);
    //const [translationChanges, setTranslationChanges] = useState([]);

    /**
     * Collect translations from i18nex
     */
    useEffect(() => {
        (async () => {
            if(componentMounted.current) {
                if(i18next.getDataByLanguage("en")) {
                    setEnglishTranslations(i18next.getDataByLanguage("en").translation);
                }
        
                if(i18next.getDataByLanguage("de")) {
                    setGermanTranslations(i18next.getDataByLanguage("de").translation);
                }         
            }
            return () => {
                componentMounted.current = false;
            }
        })();
    }, []);

    /**
     * Collect all languages into single translation array
     */
    useEffect(() => {
        if(!englishTranslations || !germanTranslations) {
            return;
        }

        let translationsArray = [];

        Object.keys(englishTranslations).forEach((key, index) => {
            const valueEN = englishTranslations[key];
            const valueDE = germanTranslations[key];
            const translation = {
                'key': key,
                'en': valueEN,
                'de': valueDE,
            }

            translationsArray.push(translation);
        });

        setTranslations(translationsArray);
    }, [englishTranslations, germanTranslations]);

    const translationChanged = (event, key, languageCode) => {
        const value = event.target.value;

        apiClient.post("api/translations/saveChanges", {translationChanges: {key, value, languageCode}}).then((response) => {}).catch((e) => {})
    }

    const reload = () => {
        i18next.reloadResources(['en', 'de']);
    }

    const translateAll = () => {
        apiClient.post("api/translations/translateAll").then((response) => {
            setGermanTranslations(response.data)
            i18next.reloadResources("de")
            store.dispatch(toggleMessageOpen(t("Translations saved!"), "success"))
        }).catch((e) =>
            store.dispatch(toggleMessageOpen(t("Can not translate!"), "error"))
        )
    }

    return (
        <>
            <Helmet>
                <title>{t("Translations")}</title>
            </Helmet>
            <Grid container justifyContent="flex-end" sx={{mb: 2}}>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={reload}
                    title={t('Refresh to get modified translations!')}
                >
                    {t("Refresh")}
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<Translate />}
                    onClick={translateAll}
                    sx={{ml: 2}}
                >
                    {t("Translate")}
                </Button>
            </Grid>

            { translations ? <TranslationTable translations={translations} setTranslations={setTranslations} translationChanged={translationChanged} /> : <LoadingScreen /> }
        </>
    )
}

export default Translations;